import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { BellIcon } from "@/components/ui/icons";
import { apiFetch } from "@/lib/api";
import type { AlertInstanceResource, Page } from "@/lib/api-types";
import { cx } from "@/lib/cx";

/**
 * The real alert bell (Story 3.9) — `NavRail`/`PhoneTopBar` previously
 * rendered `BellIcon` alone as a layout slot (VC-2.1-07 explicitly deferred
 * the real one to Epic 3). The unread count is Story 3.9's own judgement
 * call 3: "unread" and "open" are the same concept in this schema — no
 * separate read flag exists, or is invented for this story alone — so the
 * count is simply the open-instances list's own `meta.total`, the same
 * query the alert centre itself renders from (`["alert-instances",
 * "list"]"), so the two never independently drift.
 *
 * The count is always announced with a noun, never a bare digit to
 * assistive technology (EXPERIENCE.md's Accessibility Floor, VC-3.9-01) —
 * the visible badge is decorative (`aria-hidden`) and the link itself
 * carries the real `aria-label`.
 *
 * Story 17.1 — this used to take a `variant: "light" | "rail"` prop so
 * `NavRail.tsx`'s own near-black background could swap in the
 * `colors.rail-*` counterparts of its two default classes. `NavRail.tsx` is
 * gone (replaced by the floating nav bar, which stays on the ordinary light
 * tokens like every other surface) and was the only caller that ever passed
 * `"rail"` — the prop is gone with it rather than left as dead surface area
 * nothing exercises.
 */
export const ALERT_INSTANCES_LIST_QUERY_KEY = ["alert-instances", "list"] as const;

function announcedLabel(count: number): string {
  if (count === 0) {
    return "No alerts";
  }
  return count === 1 ? "1 alert" : `${count} alerts`;
}

export function AlertBell({ className }: { className?: string }) {
  const { data } = useQuery({
    queryKey: ALERT_INSTANCES_LIST_QUERY_KEY,
    queryFn: () => apiFetch<Page<AlertInstanceResource>>("/alert-instances"),
  });
  const count = data?.meta.total ?? 0;

  return (
    <Link
      to="/alerts"
      aria-label={announcedLabel(count)}
      className={cx(
        "relative flex h-tap-target-min w-tap-target-min items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover",
        className,
      )}
    >
      <BellIcon />
      {count > 0 ? (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 flex h-badge min-w-badge items-center justify-center rounded-full bg-danger px-1 text-meta font-semibold leading-none text-on-danger"
        >
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </Link>
  );
}
